import { Button, Container, Group, Title } from '@mantine/core';
import { useState } from 'react';
import { Entity } from '../../../enums/entity.enums';
import { OpenComponent } from '../../../enums/misc.enum';
import { Roles } from '../../../enums/roles-enum';
import {
  convertBasicBranchesToEnableDisableData,
  convertBasicMerchantsToEnableDisableData,
  convertBasicPartnersToEnableDisableData,
  convertBasicResellersToEnableDisableData,
} from '../../../helpers/button.helpers';
import useAuth from '../../../hooks/useAuth';
import useDidMountEffect from '../../../hooks/useDidMountEffect';
import { IPaginationMeta } from '../../../type/admin.types';
import { EnableDisableButtonData } from '../../../type/button.types';
import {
  BasicMerchant,
  BasicMerchantBranch,
  BasicPartner,
  BasicReseller,
  RetrieveAllEntityRequest,
} from '../../../type/entity.types';
import AdminUpdateMerchant from '../../admin-update-merchant/admin-update-merchant.component';
import DisableButton from '../../buttons/disable-button.component';
import EnableButton from '../../buttons/enable.button.component';
import SearchButton from '../../buttons/search-button/search-button.component';
import UpdateButton from '../../buttons/update.button.component';
import BranchesTable from '../../tables/branches-table.component';
import MerchantsTable from '../../tables/merchants-table.component';
import PartnersTable from '../../tables/partners-table.component';
import ResellersTable from '../../tables/resellers-table.component';
import XPagination from '../../x-components/x-pagination.component';
import XPaper from '../../x-components/x-paper.component';
import CreateEntityManager from '../create-entity/create-entity-manager.component';
import BranchInfo from '../view-entity/branch-info.component';
import MerchantInfo from '../view-entity/merchant-info.component';
import PartnerInfo from '../view-entity/partner-info.component';
import ResellerInfo from '../view-entity/reseller-info.component';

interface Props {
  entityType: Entity;
  partnersData?: BasicPartner[];
  resellersData?: BasicReseller[];
  merchantsData?: BasicMerchant[];
  branchesData?: BasicMerchantBranch[];
  filter?: JSX.Element;
  paginationRequest: (page: number, limit: number) => void;
  paginationData?: IPaginationMeta;
  entityParams: RetrieveAllEntityRequest;
  setEntityParams: React.Dispatch<
    React.SetStateAction<RetrieveAllEntityRequest>
  >;
  setRefreshTable: React.Dispatch<React.SetStateAction<boolean>>;
  checkedAll: boolean;
  setCheckedAll: React.Dispatch<React.SetStateAction<boolean>>;
  isChecked: number[];
  setIsChecked: React.Dispatch<React.SetStateAction<number[]>>;
  isLoading: boolean;
}

function AdminEntityManager(props: Readonly<Props>) {
  const {
    entityType,
    paginationRequest,
    paginationData,
    partnersData,
    resellersData,
    merchantsData,
    branchesData,
    filter,
    entityParams,
    setEntityParams,
    setRefreshTable,
    checkedAll,
    setCheckedAll,
    isChecked,
    setIsChecked,
    isLoading,
  } = props;

  const { auth } = useAuth();
  const isViewOnly =
    auth?.role === Roles.CHECKER || auth?.role === Roles.SETTLEMENT;

  const dynamicText = {
    titleText: '',
    createText: '',
    updateText: '',
  };

  if (entityType === Entity.BRANCH) {
    dynamicText.titleText = 'Merchant Branches';
    dynamicText.createText = 'Create branch';
    dynamicText.updateText = 'Update branch';
  } else if (entityType === Entity.MERCHANT) {
    dynamicText.titleText = 'Merchants';
    dynamicText.createText = 'Create merchant';
    dynamicText.updateText = 'Update merchant';
  } else if (entityType === Entity.RESELLER) {
    dynamicText.titleText = 'Resellers';
    dynamicText.createText = 'Create reseller';
    dynamicText.updateText = 'Update reseller';
  } else if (entityType === Entity.PARTNER) {
    dynamicText.titleText = 'Partners';
    dynamicText.createText = 'Create partner';
    dynamicText.updateText = 'Update partner';
  }

  const [search, setSearch] = useState('');
  const [selectedEntity, setSelectedEntity] = useState<number>();
  const [componentManager, setComponentManager] = useState(OpenComponent.HOME);
  const [isCheckedSimpleEntityData, setIsCheckedSimpleEntityData] = useState<
    EnableDisableButtonData | undefined
  >();

  useDidMountEffect(() => {
    setEntityParams((prev) => {
      return {
        ...prev,
        page: 1,
        searchEntity: search,
      };
    });
  }, [search]);

  useDidMountEffect(() => {
    if (isChecked.length === 1) {
      let entities: EnableDisableButtonData[] | undefined = undefined;

      if (entityType === Entity.PARTNER && partnersData) {
        entities = convertBasicPartnersToEnableDisableData(partnersData);
      } else if (entityType === Entity.RESELLER && resellersData) {
        entities = convertBasicResellersToEnableDisableData(resellersData);
      } else if (entityType === Entity.MERCHANT && merchantsData) {
        entities = convertBasicMerchantsToEnableDisableData(merchantsData);
      } else if (entityType === Entity.BRANCH && branchesData) {
        entities = convertBasicBranchesToEnableDisableData(branchesData);
      } else {
        entities = undefined;
      }

      const selectedEntity = entities?.find((entity) => {
        return entity.entityId === isChecked[0];
      });

      setIsCheckedSimpleEntityData(selectedEntity);
    } else {
      setIsCheckedSimpleEntityData(undefined);
    }
  }, [isChecked]);

  const showPartners = partnersData && selectedEntity;
  const showMerchants = merchantsData && selectedEntity;
  const showResellers = resellersData && selectedEntity;
  const showBranches = branchesData && selectedEntity;

  const isShowHome = componentManager === OpenComponent.HOME;
  const isShowViewUser = componentManager === OpenComponent.VIEW_USER;
  const isShowUpdateUser = componentManager === OpenComponent.UPDATE_USER;
  const isShowCreateUser = componentManager === OpenComponent.CREATE_USER;

  const getButtonsData = () => {
    if (entityType === Entity.PARTNER && partnersData)
      return convertBasicPartnersToEnableDisableData(partnersData);
    if (entityType === Entity.RESELLER && resellersData)
      return convertBasicResellersToEnableDisableData(resellersData);
    if (entityType === Entity.MERCHANT && merchantsData)
      return convertBasicMerchantsToEnableDisableData(merchantsData);
    if (entityType === Entity.BRANCH && branchesData)
      return convertBasicBranchesToEnableDisableData(branchesData);
  };

  return (
    <>
      {isShowHome && (
        <>
          <Title order={2} my="lg">
            {dynamicText.titleText}
          </Title>
          <XPaper>
            <Group p="md" position="apart">
              <SearchButton
                setSearch={setSearch}
                disabled={isLoading}
                search={search}
              />
              {paginationData &&
                auth?.role === Roles.ADMINISTRATOR &&
                (partnersData ||
                  resellersData ||
                  merchantsData ||
                  branchesData) && (
                  <Group
                    sx={
                      {
                        // display: 'none',
                      }
                    }
                  >
                    <EnableButton
                      isChecked={isChecked}
                      data={getButtonsData()}
                      setRefreshTable={setRefreshTable}
                    />
                    <DisableButton
                      isChecked={isChecked}
                      data={getButtonsData()}
                      setRefreshTable={setRefreshTable}
                    />
                  </Group>
                )}
            </Group>
            <Group p="md" position="right">
              {(auth?.role === Roles.ADMINISTRATOR ||
                auth?.role === Roles.PARTNER ||
                auth?.role === Roles.RESELLER) && (
                <Button
                  onClick={() => {
                    setComponentManager(OpenComponent.CREATE_USER);
                  }}
                  disabled={isLoading}
                >
                  {dynamicText.createText}
                </Button>
              )}

              {!isViewOnly && (
                <UpdateButton
                  isChecked={isChecked}
                  data={getButtonsData()}
                  setComponentManager={setComponentManager}
                  text={dynamicText.updateText}
                />
              )}
            </Group>

            <Container p="md" fluid>
              {filter}
            </Container>
            {entityType === Entity.PARTNER && (
              <PartnersTable
                partnersData={partnersData}
                setSelectedEntity={setSelectedEntity}
                setComponentManager={setComponentManager}
                isChecked={isChecked}
                setIsChecked={setIsChecked}
                checkedAll={checkedAll}
                setCheckedAll={setCheckedAll}
                isLoading={isLoading}
              />
            )}
            {entityType === Entity.RESELLER && (
              <ResellersTable
                resellersData={resellersData}
                setSelectedEntity={setSelectedEntity}
                setComponentManager={setComponentManager}
                isChecked={isChecked}
                setIsChecked={setIsChecked}
                checkedAll={checkedAll}
                setCheckedAll={setCheckedAll}
                isLoading={isLoading}
              />
            )}
            {entityType === Entity.MERCHANT && (
              <MerchantsTable
                merchantsData={merchantsData}
                setSelectedEntity={setSelectedEntity}
                setComponentManager={setComponentManager}
                isChecked={isChecked}
                setIsChecked={setIsChecked}
                checkedAll={checkedAll}
                setCheckedAll={setCheckedAll}
                isLoading={isLoading}
              />
            )}
            {entityType === Entity.BRANCH && (
              <BranchesTable
                branchesData={branchesData}
                setSelectedEntity={setSelectedEntity}
                setComponentManager={setComponentManager}
                isChecked={isChecked}
                setIsChecked={setIsChecked}
                checkedAll={checkedAll}
                setCheckedAll={setCheckedAll}
                isLoading={isLoading}
              />
            )}
            {/* Has to use ? instead of && because && will print out the totalPages when it is 0 */}
            {paginationData?.totalPages ? (
              <XPagination
                total={paginationData.totalPages}
                page={paginationData.currentPage}
                paginationRequest={paginationRequest}
                limit={entityParams.limit}
              />
            ) : (
              <></>
            )}
          </XPaper>
        </>
      )}
      {isShowViewUser && (
        <>
          {showPartners && (
            <PartnerInfo
              setComponentManager={setComponentManager}
              selectedEntity={selectedEntity}
            />
          )}
          {showMerchants && (
            <MerchantInfo
              setComponentManager={setComponentManager}
              selectedEntity={selectedEntity}
            />
          )}
          {showResellers && (
            <ResellerInfo
              setComponentManager={setComponentManager}
              selectedEntity={selectedEntity}
            />
          )}
          {showBranches && (
            <BranchInfo
              setComponentManager={setComponentManager}
              selectedEntity={selectedEntity}
            />
          )}
        </>
      )}
      {isShowUpdateUser &&
        isCheckedSimpleEntityData?.accountId !== undefined && (
          <AdminUpdateMerchant
            merchantId={isCheckedSimpleEntityData.accountId}
            setRefreshTable={setRefreshTable}
            setComponentManager={setComponentManager}
          />
        )}
      {isShowCreateUser && (
        <CreateEntityManager
          setComponentManager={setComponentManager}
          entityType={entityType}
          setRefreshTable={setRefreshTable}
        />
      )}
    </>
  );
}

export default AdminEntityManager;
